import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Plane, Calendar, TrendingDown, ExternalLink, Hotel, Check } from 'lucide-react';
import { Navbar, Footer } from '../components/Layout';
import { VisualCalendar } from '../components/VisualCalendar';
import { promotionsAPI } from '../lib/api';
import { formatCurrency, formatDate, getContinentByDestination } from '../lib/helpers';
import { getDestinationImage } from '../lib/images';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

const getHeroImage = (destination, images) => {
  if (images && images.length > 0) return images[0];
  return getDestinationImage(destination, '1600x900');
};

/* --- Sub-components --- */

const PriceSection = ({ promotion, isPackage }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
    <div className="bg-slate-50 rounded-xl p-6">
      <h3 className="text-sm font-medium text-slate-600 mb-2">
        {isPackage ? 'Preço do Pacote' : 'Preço Rota Continental'}
      </h3>
      <div className="text-3xl font-bold text-[#00A8B5]" data-testid="price-rota">
        {formatCurrency(promotion.calculated_price)}
      </div>
      <p className="text-xs text-slate-500 mt-2">
        {isPackage 
          ? 'Por pessoa - voo + hospedagem + serviços inclusos'
          : 'Preço já inclui todas as taxas e conversão de milhas'
        }
      </p>
    </div>
    {promotion.skyscanner_price && (
      <div className="bg-slate-50 rounded-xl p-6">
        <h3 className="text-sm font-medium text-slate-600 mb-2">Preço Médio Skyscanner</h3>
        <div className="text-3xl font-bold text-slate-400 line-through" data-testid="price-skyscanner">
          {formatCurrency(promotion.skyscanner_price)}
        </div>
        {promotion.savings_percentage > 0 && (
          <div className="mt-3 flex items-center gap-2">
            <TrendingDown className="w-5 h-5 text-green-600" />
            <div>
              <p className="text-xs text-slate-600">Você economiza</p>
              <p className="text-xl font-bold text-green-600" data-testid="savings">
                {promotion.savings_percentage}%
              </p>
            </div>
          </div>
        )}
      </div>
    )}
  </div>
);

const HotelSection = ({ promotion }) => {
  if (!promotion.accommodation_name) return null;
  return (
    <div className="bg-slate-50 rounded-xl p-6" data-testid="hotel-section">
      <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
        <Hotel className="w-5 h-5 text-[#00A8B5]" />
        Hospedagem
      </h2>
      <div className="flex flex-col sm:flex-row gap-5">
        {promotion.accommodation_images?.length > 0 && (
          <div className="sm:w-48 h-36 rounded-lg overflow-hidden flex-shrink-0">
            <img 
              src={promotion.accommodation_images[0]}
              alt={promotion.accommodation_name}
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-slate-900 mb-1" data-testid="hotel-name">
            {promotion.accommodation_name}
          </h3>
          {promotion.accommodation_nights && (
            <p className="text-sm text-[#00A8B5] font-medium mb-2">
              {promotion.accommodation_nights} noites
            </p>
          )}
          {promotion.accommodation_description && (
            <p className="text-sm text-slate-600 mb-3" data-testid="hotel-description">
              {promotion.accommodation_description}
            </p>
          )}
          {promotion.accommodation_amenities?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {promotion.accommodation_amenities.map((amenity) => (
                <span key={amenity} className="text-xs bg-white border border-slate-200 px-2.5 py-1 rounded-full text-slate-600">
                  {amenity}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ServicesSection = ({ services }) => {
  if (!services?.length) return null;
  return (
    <div className="bg-slate-50 rounded-xl p-6" data-testid="services-section">
      <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
        <Check className="w-5 h-5 text-[#00A8B5]" />
        O que está incluso
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {services.map((service) => (
          <div key={service} className="flex items-start gap-3 bg-white rounded-lg p-3" data-testid="included-service-item">
            <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <Check className="w-3.5 h-3.5 text-green-600" />
            </div>
            <span className="text-sm text-slate-700">{service}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const DateTable = ({ dates, isPackage }) => (
  <div className="mb-8">
    <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4">
      {isPackage ? 'Datas Disponíveis' : 'Todas as Combinações de Datas (Máximo 15 dias)'}
    </h2>
    <div className="bg-slate-50 rounded-xl overflow-hidden">
      {dates?.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-200">
              <tr>
                <th className="px-4 sm:px-6 py-3 text-left text-sm font-semibold text-slate-700">Data Ida</th>
                <th className="px-4 sm:px-6 py-3 text-left text-sm font-semibold text-slate-700">Data Volta</th>
                <th className="px-4 sm:px-6 py-3 text-left text-sm font-semibold text-slate-700">Dias</th>
                <th className="px-4 sm:px-6 py-3 text-left text-sm font-semibold text-slate-700">Preço</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {dates.map((date) => {
                const departureDate = new Date(date.departure);
                const returnDate = date.return_date ? new Date(date.return_date) : null;
                const days = returnDate ? Math.ceil((returnDate - departureDate) / (1000 * 60 * 60 * 24)) : 0;
                const rowKey = `${date.departure}-${date.return_date || 'single'}`;
                
                return (
                  <tr key={rowKey} className="hover:bg-white transition" data-testid={`date-row-${rowKey}`}>
                    <td className="px-4 sm:px-6 py-4 text-sm text-slate-900">{formatDate(date.departure)}</td>
                    <td className="px-4 sm:px-6 py-4 text-sm text-slate-900">
                      {date.return_date ? formatDate(date.return_date) : '-'}
                    </td>
                    <td className="px-4 sm:px-6 py-4 text-sm text-slate-600">
                      {days > 0 ? `${days} dias` : '-'}
                    </td>
                    <td className="px-4 sm:px-6 py-4 text-sm font-semibold text-[#00A8B5]">
                      {formatCurrency(date.price)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="p-8 text-center text-slate-600">
          <p>Nenhuma combinação de datas disponível no momento.</p>
          <p className="text-sm mt-2">Entre em contato para mais informações.</p>
        </div>
      )}
    </div>
  </div>
);

/* --- Main Component --- */

const PromotionDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [promotion, setPromotion] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadPromotion = useCallback(async () => {
    try {
      const response = await promotionsAPI.getById(id);
      setPromotion(response.data);
    } catch {
      // silently handled - UI shows not found
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadPromotion();
  }, [loadPromotion]);

  const handleWhatsAppClick = () => {
    const type = promotion.is_package ? 'pacote' : 'promocao';
    const message = encodeURIComponent(
      `Ola! Tenho interesse no ${type}: ${promotion.origin} \u2192 ${promotion.destination} por ${formatCurrency(promotion.calculated_price)}`
    );
    window.open(`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER}?text=${message}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00A8B5]"></div>
      </div>
    );
  }

  if (!promotion) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Promoção não encontrada</h2>
          <Button onClick={() => navigate('/')}>Voltar para Início</Button>
        </div>
      </div>
    );
  }

  const continent = getContinentByDestination(promotion.destination);
  const imageUrl = getHeroImage(promotion.destination, promotion.accommodation_images);
  const isPackage = promotion.is_package;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="pt-24 pb-16 px-4">
        <div className="max-w-5xl mx-auto">
          <button
            onClick={() => navigate(isPackage ? '/pacotes' : '/')}
            className="flex items-center gap-2 text-slate-600 hover:text-[#00A8B5] mb-8 transition"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
            {isPackage ? 'Voltar para Pacotes' : 'Voltar para Promoções'}
          </button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-lg overflow-hidden"
          >
            <div className="relative h-72 sm:h-80 overflow-hidden">
              <img 
                src={imageUrl}
                alt={promotion.destination}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 text-white">
                <div className="flex flex-wrap gap-2 mb-3">
                  <Badge variant="secondary" className="bg-white/90 backdrop-blur-sm text-slate-900 text-xs">
                    {continent}
                  </Badge>
                  {isPackage && (
                    <Badge className="bg-[#00A8B5] text-white text-xs">
                      PACOTE COMPLETO
                    </Badge>
                  )}
                </div>
                <h1 className="text-2xl sm:text-4xl font-bold mb-2" data-testid="promotion-title">
                  {promotion.origin} &rarr; {promotion.destination}
                </h1>
                <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-white/90 text-sm">
                  <span>{promotion.airline}</span>
                  <span>&middot;</span>
                  <span>{promotion.cabin}</span>
                  <span>&middot;</span>
                  <span>{promotion.trip_type}</span>
                </div>
              </div>
            </div>

            <div className="p-6 sm:p-8">
              <PriceSection promotion={promotion} isPackage={isPackage} />

              {isPackage && (
                <div className="mb-8 space-y-6">
                  <HotelSection promotion={promotion} />
                  <ServicesSection services={promotion.included_services} />
                </div>
              )}

              <div className="mb-8">
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[#00A8B5]" />
                  Calendário de Disponibilidade
                </h2>
                <VisualCalendar 
                  outboundDates={promotion.outbound_dates || []}
                  returnDates={promotion.return_dates || []}
                  savingsPercentage={promotion.savings_percentage}
                />
              </div>

              <DateTable dates={promotion.available_dates} isPackage={isPackage} />

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={handleWhatsAppClick}
                  className="flex-1 bg-[#00A8B5] hover:bg-[#008C99] text-white font-semibold py-6 text-base sm:text-lg rounded-full"
                  data-testid="request-ticket-button"
                >
                  <Plane className="w-5 h-5 mr-2" />
                  {isPackage ? 'Quero este Pacote' : 'Quero Emitir essa Passagem'}
                </Button>
                {promotion.airline_link && (
                  <Button
                    onClick={() => window.open(promotion.airline_link, '_blank')}
                    variant="outline"
                    className="flex-1 py-6 text-base sm:text-lg rounded-full"
                    data-testid="airline-link-button"
                  >
                    <ExternalLink className="w-5 h-5 mr-2" />
                    Site da Companhia
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PromotionDetails;
